export interface PlatformSignals {
  readonly userAgent: string;
  readonly platform: string;
  readonly maxTouchPoints: number;
  readonly hints?: {
    readonly platform?: string;
    readonly architecture?: string;
    readonly bitness?: string;
    readonly mobile?: boolean;
  };
}

export interface DownloadRecommendation {
  readonly platform: 'windows' | 'macos' | '';
  readonly architecture: 'x86_64' | 'universal' | '';
  readonly message: string;
}

export function recommendNativeDownload(signals: PlatformSignals): DownloadRecommendation {
  const mobile =
    signals.hints?.mobile ||
    /Android|iPhone|iPad|iPod/i.test(signals.userAgent) ||
    (/Mac/i.test(signals.platform) && signals.maxTouchPoints > 1);
  if (mobile)
    return {
      platform: '',
      architecture: '',
      message:
        'Native installers are available for Windows and macOS computers. Choose the computer you want to install on.',
    };
  if (signals.hints?.platform === 'macOS' || /Mac/i.test(signals.platform)) {
    return {
      platform: 'macos',
      architecture: 'universal',
      message:
        'Recommended for this Mac. The universal installer supports Apple silicon and Intel.',
    };
  }
  if (signals.hints?.platform === 'Windows' || /Win/i.test(signals.platform)) {
    const reliableX64 = signals.hints?.architecture === 'x86' && signals.hints?.bitness === '64';
    return {
      platform: 'windows',
      architecture: reliableX64 ? 'x86_64' : '',
      message: reliableX64
        ? 'Recommended for this Windows PC.'
        : signals.hints?.architecture === 'arm'
          ? 'Windows on ARM is not supported by the current installer. You can download for another computer.'
          : 'Windows detected. Confirm your system type in Settings → System → About before choosing an installer.',
    };
  }
  return {
    platform: '',
    architecture: '',
    message:
      'Choose Windows or macOS to download for another computer. This platform does not have a native installer.',
  };
}

interface HintNavigator extends Navigator {
  readonly userAgentData?: {
    readonly platform: string;
    readonly mobile: boolean;
    getHighEntropyValues(
      hints: string[],
    ): Promise<{ platform?: string; architecture?: string; bitness?: string; mobile?: boolean }>;
  };
}

export async function detectNativeDownload(
  source: HintNavigator = navigator,
): Promise<DownloadRecommendation> {
  const signals = {
    userAgent: source.userAgent,
    platform: source.platform,
    maxTouchPoints: source.maxTouchPoints,
  };
  try {
    if (source.userAgentData) {
      // A browser may reduce or refuse architecture hints. Never guess x64 from its UA string.
      const hints = await Promise.race([
        source.userAgentData.getHighEntropyValues(['architecture', 'bitness', 'platform']),
        new Promise<undefined>((resolve) => setTimeout(resolve, 700)),
      ]);
      return recommendNativeDownload({ ...signals, hints: { ...source.userAgentData, ...hints } });
    }
  } catch {
    /* Manual selection remains available when client hints are denied. */
  }
  return recommendNativeDownload(signals);
}
